import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbar } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { catchError, of, Subject, takeUntil, tap, throwError } from 'rxjs';
import { InformationPromotions } from 'src/app/shared/interfaces/promotions/InformationPromotions';
import { PromotionsDetail } from 'src/app/shared/interfaces/promotions/PromotionsDetail';
import { PromotionsService } from 'src/app/shared/services/promotions.service';
import { PromotionsInfoComponent } from '../promotions-info/promotions-info.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { PromotionStatutChipComponent } from 'src/app/shared/components/promotion-statut-chip/promotion-statut-chip.component';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from 'src/app/shared/components/generic-dialog/generic-dialog.component';
import { EanOrphelinAttachementComponent } from '../ean-orphelin-attachement/ean-orphelin-attachement.component';
import { EanOrphelin } from 'src/app/shared/interfaces/promotions/EanOrphelin';
import { ConfirmationDialog } from 'src/app/shared/components/alert-dialog/confirmation-dialog.component';
import { SnackbarHelper } from 'src/app/shared/services/snackbar/snackbar.helper';
import { SnackbarService } from 'src/app/shared/services/snackbar/snackbar.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PromotionDetailsService } from '../../services/promotion-details-service';
import { CustomNumberFormatPipe } from 'src/app/shared/pipe/custom-number-format.pipe';

@Component({
  selector: 'app-promotions-detail',
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    MatSortModule,
    MatTableModule,
    MatSelectModule,
    MatPaginatorModule,
    MatToolbar,
    CommonModule,
    PromotionsInfoComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    RouterModule,
    PromotionStatutChipComponent,
    MatTooltipModule,
    CustomNumberFormatPipe,
  ],
  templateUrl: './promotions-detail.component.html',
  styleUrl: './promotions-detail.component.scss',
})
export class PromotionsDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  displayedColumns: string[] = [
    'dettacher',
    'codeEan',
    'designation',
    'tva',
    'quantiteVendue',
    'montantVendu',
    'totalCompensationCalculee',
  ];
  private readonly _destroying$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource: MatTableDataSource<PromotionsDetail>;
  informationPromotions?: InformationPromotions;
  globalFilter: string = '';
  statusFilter?: string[] = ['A valider', 'Terminée'];
  selectedStatut!: string;
  selectedStatutOption!: string;
  idPromotion = 0;
  idAdherent = 0;
  listEanOrphelin: EanOrphelin[] = [];
  listEan: PromotionsDetail[] = [];

  constructor(
    private readonly promotionsService: PromotionsService,
    private readonly promotionDetailsService: PromotionDetailsService,
    private readonly dialog: MatDialog,
    private readonly snackbarService: SnackbarService,
  ) {
    this.dataSource = new MatTableDataSource<PromotionsDetail>([]);
    this.sort = new MatSort();
  }

  ngOnInit(): void {
    const promotion = this.promotionDetailsService.getPromotionDetails();

    if (promotion != null) {
      this.idPromotion = promotion.identifiantPromo;
      this.idAdherent = promotion.idAdherent;
    }

    if (this.idPromotion && this.idAdherent) {
      this.getPromotionsDetail(this.idPromotion, this.idAdherent);
      this.getEanOrphelin(this.idAdherent, this.idPromotion);
    }

    this.dataSource.filterPredicate = (data: PromotionsDetail, filter: string) => {
      const transformedFilter = filter.trim().toLowerCase();
      return (
        (data.ean?.toLowerCase().includes(transformedFilter) ?? false) ||
        (data.designation?.toLowerCase().includes(transformedFilter) ?? false)
      );
    };
  }

  getStatutSelected(statut: MatSelectChange) {
    this.selectedStatutOption = statut.value;
    this.updatePromotionStatut(this.idPromotion, this.idAdherent, this.selectedStatutOption);
  }

  updatePromotionStatut(idPromotion: number, idAdherent: number, selectedStatut: string) {
    this.promotionsService
      .updatePromotionStatut(idPromotion, idAdherent, selectedStatut)
      .pipe(
        takeUntil(this._destroying$),
        tap(promotions => {}),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getPromotionsDetail(idPromotion: number, idAdherent: number) {
    this.promotionsService
      .getPromotionsDetailsByPromotionIdAndAdherentId(idPromotion, idAdherent)
      .pipe(
        takeUntil(this._destroying$),
        tap(Vuepromotions => {
          this.dataSource.data = Vuepromotions.listPromotionsDetailDTO;
          this.listEan = Vuepromotions.listPromotionsDetailDTO;
          this.informationPromotions = Vuepromotions.informationPromotionsDTO;
          this.selectedStatut = this.informationPromotions.statut;
        }),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe();
  }

  getEanOrphelin(idAdherent: number, idPromotion: number) {
    this.promotionsService
      .getEanOrphelinByPromotionIdAndAdherent(idAdherent, idPromotion)
      .pipe(
        takeUntil(this._destroying$),
        tap((EanOrphelin: EanOrphelin[]) => {
          this.listEanOrphelin = EanOrphelin;
        }),
        catchError(() => {
          return of([]);
        }),
      )
      .subscribe();
  }

  applyFilter(filterValue: string): void {
    this.globalFilter = filterValue;
    this.dataSource.filter = this.globalFilter.trim().toLowerCase();
  }

  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  rattacherEanOrphelin() {
    const dialogRef = this.dialog.open(GenericDialogComponent, {
      minWidth: '50vh',
      maxWidth: '60vw',
      maxHeight: '80vh',
      panelClass: 'orphelin-dialog',
      data: {
        title: 'Rattacher un code orphelin',
        component: EanOrphelinAttachementComponent,
        componentData: {
          eanOrphelinList: this.listEanOrphelin,
          eanList: this.listEan,
        },
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getPromotionsDetail(this.idPromotion, this.idAdherent);
      this.getEanOrphelin(this.idAdherent, this.idPromotion);
    });
  }

  dettacherEanOrphelin(promotionsDetail: PromotionsDetail) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        message: `Êtes-vous sûr(e) de vouloir détacher l'EAN orphelin "${promotionsDetail.eanOrphelin}" ?`,
        buttonText: {
          ok: 'Oui',
          cancel: 'Non',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.promotionsService
          .linkOffEanOrphelinFromPromotionProduct(promotionsDetail.id)
          .pipe(
            takeUntil(this._destroying$),
            catchError(error => {
              this.snackbarService.show(
                "Une erreur s'est produite lors du détachement d'EAN orphelin.",
                SnackbarHelper.snackbarPanelClassError(),
              );
              return throwError(
                () =>
                  new Error(
                    `Une erreur s'est produite lors du détachement d'EAN orphelin.\n${error}`,
                  ),
              );
            }),
          )
          .subscribe({
            next: () => {},
            error: () => {},
            complete: () => {
              this.snackbarService.show(
                'Le détachement a bien été effectué.',
                SnackbarHelper.snackbarPanelClassSuccess(),
              );
              this.getPromotionsDetail(this.idPromotion, this.idAdherent);
              this.getEanOrphelin(this.idAdherent, this.idPromotion);
            },
          });
      }
    });
  }

  public get dialogInstance() {
    return this.dialog;
  }

  /**
   * Generates a detailed PDF report for promotions using jsPDF and autoTable.
   * The PDF includes:
   * - A structured header with logos and titles.
   * - A summary table with key promotion details.
   * - A detailed product table with conditional styling.
   * - Page numbering and branding.
   * - A dynamically generated file name with timestamps.
   */
  generatePDF(): void {
    const doc = new jsPDF();
    let y = 20; // Initial vertical position

    // ✅ Add "INFORMATIONS GLOBALES" section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIONS GLOBALES', 14, y);
    y += 7;

    // Define promotion details table data
    const data: string[][] = [
      [
        `Laboratoire : ${this.informationPromotions?.nomLaboratoire ?? '-'}`,
        `Total Quantité : ${this.informationPromotions?.quantiteVendue ?? '-'}`,
        `Offre : ${this.informationPromotions?.offre ?? '-'}`,
      ],
      [
        `ID Promo : ${this.informationPromotions?.idPromotion ?? '-'}`,
        `Total Sell-Out : ${(this.informationPromotions?.montantVendu ?? 0).toFixed(2)} €`,
        '',
      ],
      [
        `Période : ${this.formatDate(
          this.informationPromotions?.dateDebut ?? '',
        )} - ${this.formatDate(this.informationPromotions?.dateFin ?? '')}`,
        `Mode de Compensation : ${this.informationPromotions?.modeCompensation ?? '-'}`,
        '',
      ],
      [
        `Gamme : ${this.informationPromotions?.nomGamme ?? '-'}`,
        `Type de Compensation : ${this.informationPromotions?.typeCompensation ?? '-'}`,
        '',
      ],
      [
        `Condition de Sell-In : ${this.informationPromotions?.conditionSellIn ?? '-'}`,
        `Montant des Compensations Calculées : ${(
          this.informationPromotions?.montantCompensationCalculee ?? 0
        ).toFixed(2)} €`,
        '',
      ],
    ];

    // Generate table for promotion details
    (doc as any).autoTable({
      body: data,
      startY: y,
      theme: 'plain', // No internal borders
      styles: {
        cellPadding: 3,
        fontSize: 10,
        halign: 'left',
        lineWidth: 0,
      },
    });

    // ✅ Draw a rounded rectangle around "INFORMATIONS GLOBALES" table
    const finalY = (doc as any).lastAutoTable.finalY;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const borderRadius = 5;

    doc.setLineWidth(0.2);
    (doc as any).roundedRect(
      margin,
      y - 5,
      pageWidth - margin * 2,
      finalY - y + 10,
      borderRadius,
      borderRadius,
    );

    y = finalY + 15; // Adjust vertical position

    // ✅ Add "DETAILS PRODUITS" section title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DETAILS PRODUITS', 14, y);
    y += 2;

    // ✅ Prepare data for product details table
    let tableData: (string | number | boolean)[][] = [];
    if (this.dataSource.data.length > 0) {
      tableData = this.dataSource.data.map((element: any) => [
        element.eanOrphelin ?? element.ean,
        element.designationOrphelin ?? element.designation,
        element.tvaOrphelin ?? element.tva,
        element.quantiteVendue,
        element.montantVendu ? `${element.montantVendu.toFixed(2)} €` : '',
        element.montantCompensationCalculee
          ? `${element.montantCompensationCalculee.toFixed(2)} €`
          : '',
        element.flagEanOrphelin,
      ]);
    }

    // ✅ Generate product table with conditional styling
    if (tableData.length === 0) {
      tableData = [['Aucune donnée disponible']];
    }

    autoTable(doc, {
      startY: y,
      head: [
        ['Code EAN', 'Désignation', 'TVA', 'Quantité Vendue', 'Total SELL-OUT', 'Compensation'],
      ],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      didParseCell: data => {
        const rowIndex = data.row.index;
        const isOrphelin = tableData[rowIndex][6];

        if (isOrphelin) {
          data.cell.styles.fillColor = [230, 230, 250]; // Lavender color for orphaned products
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [50, 50, 50];
        }
      },
      didDrawPage: data => {
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // ✅ Footer: Page number
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Page ${data.pageNumber}`, pageWidth / 2, pageHeight - 10, {
          align: 'center',
        });
      },
    });

    // ✅ Save the PDF with a timestamped filename
    const now = new Date();
    const formattedDate =
      now.getFullYear().toString() +
      ('0' + (now.getMonth() + 1)).slice(-2) +
      ('0' + now.getDate()).slice(-2) +
      '_' +
      ('0' + now.getHours()).slice(-2) +
      ('0' + now.getMinutes()).slice(-2) +
      ('0' + now.getSeconds()).slice(-2);

    const fileName =
      'promotion_détails_' +
      this.informationPromotions?.nomLaboratoire +
      '_' +
      this.informationPromotions?.idPromotion +
      '_' +
      formattedDate +
      '.pdf';

    doc.save(fileName);
  }

  // ✅ Fonction pour formater la date en "dd/MM/yyyy"
  formatDate(date: string | Date | undefined): string {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      // Retourne la date formatée
      return date.toLocaleDateString('fr-FR'); // ou selon ton format préféré
    }
    return 'Date invalide'; // Ou gérer autrement si la date n'est pas valide
  }

  // ✅ Fonction pour formater un montant en EUR avec 2 décimales
  formatCurrency(amount?: number): string {
    return amount !== undefined && amount !== null ? `${amount.toFixed(2)} €` : '';
  }
}
